// Login page — T057
import { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAppSettings } from '../hooks/useAppSettings';

const OAUTH_ERRORS: Record<string, string> = {
  oauth_denied: 'Google sign-in was cancelled.',
  no_code: 'No authorization code received from Google.',
  token_exchange: 'Failed to exchange Google authorization code.',
  userinfo_failed: 'Could not retrieve your Google profile.',
  no_email: 'No email address found in your Google account.',
  server_error: 'An unexpected error occurred during sign-in.',
  oauth_failed: 'OAuth sign-in failed. Please try again.',
};

export default function Login() {
  const { login } = useAuth();
  const { appTitle } = useAppSettings();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Show OAuth errors from redirect query params
  useEffect(() => {
    const oauthError = searchParams.get('error');
    if (oauthError) {
      setError(OAUTH_ERRORS[oauthError] || `Sign-in error: ${oauthError}`);
    }
  }, [searchParams]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
      <div className="bg-surface rounded-xl p-8 w-full max-w-md border border-border">
        <h1 className="text-white text-h1 text-center mb-2">{appTitle}</h1>
        <p className="text-text-secondary text-center mb-8">Sign in to your account</p>

        {error && (
          <div className="bg-status-down/20 text-status-down rounded-lg px-4 py-3 mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-text-secondary text-sm mb-1">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-bg-primary text-text-primary border border-border rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              placeholder="admin@cm-monitor.local"
              required
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-text-secondary text-sm mb-1">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-bg-primary text-text-primary border border-border rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              placeholder="••••••••"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand hover:bg-brand-hover text-white font-medium rounded-lg px-4 py-3 min-h-[44px] transition-colors disabled:opacity-50"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="mt-6 pt-6 border-t border-border">
          <a
            href={`${import.meta.env.VITE_API_BASE || '/api'}/auth/oauth/google`}
            className="w-full flex items-center justify-center gap-2 bg-surface-hover hover:bg-border text-text-primary font-medium rounded-lg px-4 py-3 min-h-[44px] transition-colors"
          >
            Sign in with Google
          </a>
        </div>
      </div>
    </div>
  );
}
